import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type EmploymentHistoryEntry = {
  _id: string;
  startDate: string;
  endDate: string;
  position: string;
  salaryAmount: number;
  salaryType: "annual" | "daily" | "hourly" | "biweekly";
  currency: string;
};

export type EmploymentHistoryData = {
  employmentHistory: EmploymentHistoryEntry[];
  consecutiveYearsOfService: number;
};

export type EmploymentHistoryResponse = {
  success: boolean;
  data: EmploymentHistoryData;
};

const getEmploymentHistory = async (
  userId: string
): Promise<EmploymentHistoryResponse> => {
  const { data } = await customAxios.get<EmploymentHistoryResponse>(
    `/users/${userId}/employment-history`
  );
  return data;
};

export const useEmploymentHistory = (userId: string, enabled = true) => {
  const queryClient = useQueryClient();

  return useQuery<EmploymentHistoryResponse, AxiosError>(
    ["employmentHistory", userId],
    () => getEmploymentHistory(userId),
    {
      enabled: !!userId && enabled,
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to fetch employment history",
          color: "red",
        }),
    }
  );
};
